// The mobile bottom sheets: Create, your profile, Settings, inbox buckets, workspace switcher, task
// settings, notepad and notification mute. One is open at a time (sheets.ts).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { format, isToday } from 'date-fns';
import {
  Activity,
  Archive,
  Bell,
  BellOff,
  CalendarRange,
  Check,
  CheckCheck,
  CircleCheck,
  Clock,
  Copy,
  CopyPlus,
  FileText,
  Folder,
  Grid3x3,
  Hash,
  Inbox,
  Info,
  KeyRound,
  Link2,
  ListTodo,
  LogOut,
  Mail,
  NotebookPen,
  Pencil,
  SendHorizontal,
  Settings,
  SlidersHorizontal,
  Star,
  Trash2,
  UserPlus,
  Users,
} from '@/components/ui/icons';
import { spacesOf } from '@/lib/hierarchy';
import { useDb, useMe, signOut } from '@/lib/store';
import { useOpenTask, useWorkspaceId } from '@/lib/nav';
import { useIsGuest } from '@/lib/roles';
import { applyTheme, NOTIFY_MUTED_FOREVER, notificationsMuted, useUi } from '@/lib/ui';
import { archiveTask, createDoc, deleteTask, duplicateTask, toggleFavorite, toggleWatcher } from '@/lib/actions';
import { canManage, isGuest, roleOf } from '@shared/roles';
import { Avatar } from '@/components/ui/Avatar';
import { toast } from '@/components/ui/overlays';
import { WorkspaceLogo } from '@/shell/WorkspaceLogo';
import { InviteModal } from '@/shell/InviteModal';
import { Notepad } from '@/shell/Notepad';
import { StatusField } from '@/shell/RailTools';
import { openDialog } from '@/shell/dialogs';
import { INBOX_TABS, inTab, type InboxTab } from '@/pages/inbox/model';
import { useInboxPrefs } from '@/pages/inbox/prefs';
import { version as appVersion } from '../../package.json';
import { Sheet, SheetRow, SheetSection } from './Sheet';
import { closeSheet, openSheet, useSheet } from './sheets';

export function MobileSheets() {
  const open = useSheet((s) => s.open);
  return (
    <>
      <CreateSheet open={open === 'create'} onInvite={() => openSheet('invite')} />
      <ProfileSheet open={open === 'profile'} />
      <SettingsSheet open={open === 'settings'} onInvite={() => openSheet('invite')} />
      <InboxTabsSheet open={open === 'inbox-tabs'} />
      <WorkspacesSheet open={open === 'workspaces'} />
      <TaskMenuSheet open={open === 'task-menu'} />
      <MuteSheet open={open === 'mute'} />
      <Sheet open={open === 'notepad'} onClose={closeSheet} title="Notepad" full className="m-notepad">
        <Notepad />
      </Sheet>
      <InviteModal open={open === 'invite'} onClose={closeSheet} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Create

function CreateSheet({ open, onInvite }: { open: boolean; onInvite: () => void }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const guest = useIsGuest();
  const firstSpace = useDb((db) => spacesOf(db, ws)[0]?.id);
  const run = (fn: () => void) => () => {
    closeSheet();
    fn();
  };
  return (
    <Sheet open={open} onClose={closeSheet} title="Create">
      <SheetRow icon={<CircleCheck size={20} className="m-c-task" />} label="Task" onClick={run(() => useUi.setState({ createTaskOpen: true }))} />
      {!guest && firstSpace && <SheetRow icon={<ListTodo size={20} className="m-c-list" />} label="List" onClick={run(() => openDialog({ kind: 'list', spaceId: firstSpace, folderId: null }))} />}
      <SheetRow icon={<SendHorizontal size={20} className="m-c-chat" />} label="Chat message" onClick={run(() => navigate(`/${ws}/chat?new=dm`))} />
      <SheetSection title="More">
        {!guest && <SheetRow icon={<Grid3x3 size={20} className="m-c-space" />} label="Space" onClick={run(() => openDialog({ kind: 'space' }))} />}
        {!guest && <SheetRow icon={<Hash size={20} className="m-c-channel" />} label="Channel" onClick={run(() => navigate(`/${ws}/chat?new=channel`))} />}
        <SheetRow icon={<Folder size={20} className="m-c-channel" />} label="Chat section" onClick={run(() => openDialog({ kind: 'chat-section', workspaceId: ws }))} />
        {!guest && <SheetRow icon={<FileText size={20} className="m-c-doc" />} label="Doc" onClick={run(() => navigate(`/${ws}/docs/${createDoc(ws, null).doc.id}`))} />}
        <SheetRow icon={<NotebookPen size={20} className="m-c-note" />} label="Note" onClick={() => openSheet('notepad')} />
        {!guest && <SheetRow icon={<UserPlus size={20} />} label="Invite people" onClick={onInvite} />}
      </SheetSection>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Profile

function muteLabel(until: number) {
  if (until >= NOTIFY_MUTED_FOREVER) return 'Until you turn them back on';
  return `Until ${format(until, isToday(until) ? 'h:mm a' : 'EEE h:mm a')}`;
}

function signOutNow(navigate: (p: string) => void) {
  closeSheet();
  window.atriumDesktop?.setBadgeCount(0);
  void signOut();
  navigate('/login');
}

function ProfileSheet({ open }: { open: boolean }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const user = useDb((d) => d.users[me]);
  const navigate = useNavigate();
  const until = useUi((s) => s.notifyMutedUntil);
  const muted = notificationsMuted({ notifyMutedUntil: until });
  const go = (path: string) => () => {
    closeSheet();
    navigate(`/${ws}/${path}`);
  };
  if (!user) return null;
  return (
    <Sheet open={open} onClose={closeSheet} full className="m-profile">
      <div className="m-profile-head">
        <Avatar userId={me} size={96} className="m-profile-photo" />
        <div className="m-profile-id">
          <div className="m-profile-name">{user.name}</div>
          {user.title && <div className="m-profile-title">{user.title}</div>}
        </div>
      </div>
      <div className="m-profile-status">
        <StatusField />
      </div>
      <SheetRow icon={<CalendarRange size={20} />} label="Planner" chevron onClick={go('planner')} />
      <SheetRow icon={<Pencil size={20} />} label="Edit profile" chevron onClick={go('settings?tab=profile')} />
      <SheetSection title="Details">
        <SheetRow icon={<Mail size={20} />} label="Email" value={user.email} />
      </SheetSection>
      <SheetSection title="Notifications">
        <SheetRow icon={muted ? <BellOff size={20} /> : <Bell size={20} />} label={muted ? 'Notifications muted' : 'Mute notifications'} value={muted && until ? muteLabel(until) : undefined} onClick={() => openSheet('mute')} />
        <SheetRow icon={<SlidersHorizontal size={20} />} label="Customize" chevron onClick={go('settings?tab=notifications')} />
      </SheetSection>
      <SheetSection title="General">
        <SheetRow icon={<Settings size={20} />} label="Settings" chevron onClick={() => openSheet('settings')} />
        <SheetRow icon={<Info size={20} />} label="About Atrium" chevron onClick={go('settings?tab=about')} />
        <SheetRow icon={<LogOut size={20} />} label="Log out" danger onClick={() => signOutNow(navigate)} />
      </SheetSection>
      <div className="m-profile-version">Atrium Mobile · v{appVersion}</div>
    </Sheet>
  );
}

function MuteSheet({ open }: { open: boolean }) {
  const until = useUi((s) => s.notifyMutedUntil);
  const muted = notificationsMuted({ notifyMutedUntil: until });
  const mute = (ms: number | null) => {
    useUi.setState({ notifyMutedUntil: ms });
    closeSheet();
    toast(ms ? 'Notifications muted' : 'Notifications are back on');
  };
  const tomorrow9 = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  };
  return (
    <Sheet open={open} onClose={closeSheet} title="Mute notifications">
      {muted && until && (
        <SheetSection title={muteLabel(until)}>
          <SheetRow icon={<Bell size={20} />} label="Unmute" onClick={() => mute(null)} />
        </SheetSection>
      )}
      <SheetSection title={muted ? 'Change' : undefined}>
        <SheetRow label="For 30 minutes" onClick={() => mute(Date.now() + 30 * 60_000)} />
        <SheetRow label="For 1 hour" onClick={() => mute(Date.now() + 60 * 60_000)} />
        <SheetRow label="For 2 hours" onClick={() => mute(Date.now() + 2 * 60 * 60_000)} />
        <SheetRow label="Until tomorrow" value="9:00 AM" onClick={() => mute(tomorrow9())} />
        <SheetRow label="Until I turn them back on" onClick={() => mute(NOTIFY_MUTED_FOREVER)} />
      </SheetSection>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Settings

const LAUNCH_KEY = 'atrium.mobileLaunch';
export const LAUNCH_PAGES = [
  { key: 'home', label: 'Home' },
  { key: 'inbox', label: 'Inbox' },
  { key: 'my-tasks', label: 'My Tasks' },
  { key: 'chat', label: 'Chat' },
] as const;
export function launchPage(): string {
  try {
    return localStorage.getItem(LAUNCH_KEY) ?? 'home';
  } catch {
    return 'home';
  }
}

function ThemeCard({ mode, label, on, onPick }: { mode: 'light' | 'dark' | 'system'; label: string; on: boolean; onPick: () => void }) {
  return (
    <button type="button" className={clsx('m-theme', { on })} onClick={onPick} aria-pressed={on}>
      <span className={`m-theme-art ${mode}`}>
        <span className="m-theme-screen">
          <span className="m-theme-bar" />
          <span className="m-theme-line" />
          <span className="m-theme-blocks">
            <span />
            <span />
            <span />
          </span>
          <span className="m-theme-line short" />
        </span>
      </span>
      <span className="m-theme-label">{label}</span>
    </button>
  );
}

function SettingsSheet({ open, onInvite }: { open: boolean; onInvite: () => void }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const theme = useUi((s) => s.theme);
  const role = useDb((d) => roleOf(d.workspaces[ws], me));
  const [launch, setLaunch] = useState(launchPage);
  const setTheme = (t: 'dark' | 'light' | 'system') => {
    useUi.setState({ theme: t });
    applyTheme(t);
  };
  const go = (path: string) => () => {
    closeSheet();
    navigate(`/${ws}/${path}`);
  };
  const cycleLaunch = () => {
    const i = LAUNCH_PAGES.findIndex((p) => p.key === launch);
    const next = LAUNCH_PAGES[(i + 1) % LAUNCH_PAGES.length].key;
    setLaunch(next);
    try {
      localStorage.setItem(LAUNCH_KEY, next);
    } catch {
      /* private mode */
    }
  };
  return (
    <Sheet open={open} onClose={closeSheet} title="Settings" full>
      <SheetSection title="Appearance">
        <div className="m-themes">
          <ThemeCard mode="light" label="Light" on={theme === 'light'} onPick={() => setTheme('light')} />
          <ThemeCard mode="dark" label="Dark" on={theme === 'dark'} onPick={() => setTheme('dark')} />
          <ThemeCard mode="system" label="Auto" on={theme === 'system'} onPick={() => setTheme('system')} />
        </div>
      </SheetSection>
      <SheetSection title="Preferences">
        <SheetRow icon={<Inbox size={20} />} label="Default launch page" value={LAUNCH_PAGES.find((p) => p.key === launch)?.label} onClick={cycleLaunch} />
        <SheetRow icon={<Bell size={20} />} label="Notifications" chevron onClick={go('settings?tab=notifications')} />
        <SheetRow icon={<SlidersHorizontal size={20} />} label="Text size & more" chevron onClick={go('settings?tab=appearance')} />
      </SheetSection>
      {!isGuest(role) && (
        <SheetSection title="Workspace">
          <SheetRow icon={<UserPlus size={20} />} label="Invite users" onClick={onInvite} />
          <SheetRow icon={<Users size={20} />} label="Manage users" chevron onClick={go('settings?tab=members')} />
          {canManage(role) && <SheetRow icon={<WorkspaceLogo workspaceId={ws} size={22} />} label="Edit workspace info" chevron onClick={go('settings')} />}
        </SheetSection>
      )}
      <SheetSection title="Account">
        <SheetRow icon={<KeyRound size={20} />} label="Change password" chevron onClick={go('settings?tab=profile')} />
        <SheetRow icon={<LogOut size={20} />} label="Log out" danger onClick={() => signOutNow(navigate)} />
      </SheetSection>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Inbox buckets

const TAB_ICON: Record<InboxTab, typeof Inbox> = { primary: Inbox, other: Activity, later: Clock, cleared: CheckCheck };

function InboxTabsSheet({ open }: { open: boolean }) {
  const me = useMe();
  const [prefs, setPrefs] = useInboxPrefs();
  const unread = useDb((d) => {
    const out: Record<string, number> = {};
    for (const n of Object.values(d.notifications)) if (n.userId === me && !n.readAt) for (const t of INBOX_TABS) if (inTab(n, t.key)) out[t.key] = (out[t.key] ?? 0) + 1;
    return JSON.stringify(out);
  });
  const counts = JSON.parse(unread) as Record<string, number>;
  return (
    <Sheet open={open} onClose={closeSheet} title="Inbox">
      {INBOX_TABS.map((t) => {
        const Icon = TAB_ICON[t.key];
        return (
          <SheetRow
            key={t.key}
            icon={<Icon size={20} />}
            label={t.label}
            onClick={() => (setPrefs({ tab: t.key }), closeSheet())}
            right={prefs.tab === t.key ? <Check size={18} className="m-check" /> : counts[t.key] && t.key !== 'cleared' ? <span className="m-dot" /> : undefined}
          />
        );
      })}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Workspaces

function WorkspacesSheet({ open }: { open: boolean }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const workspaces = useDb((d) => d.workspaces);
  return (
    <Sheet open={open} onClose={closeSheet} title="Workspaces">
      {Object.values(workspaces)
        .filter((w) => w.memberIds.includes(me))
        .map((w) => (
          <SheetRow
            key={w.id}
            icon={<WorkspaceLogo workspaceId={w.id} size={28} />}
            label={w.name}
            right={w.id === ws ? <Check size={18} className="m-check" /> : undefined}
            onClick={() => (closeSheet(), navigate(`/${w.id}/home`))}
          />
        ))}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Task settings (the ⋯ on the mobile task page)

function TaskMenuSheet({ open }: { open: boolean }) {
  const taskId = useSheet((s) => s.taskId);
  const me = useMe();
  const task = useDb((d) => (taskId ? d.tasks[taskId] : undefined));
  const list = useDb((d) => (task ? d.lists[task.listId] : undefined));
  const fav = useDb((d) => !!taskId && Object.values(d.favorites).some((f) => f.userId === me && f.target.type === 'task' && f.target.id === taskId));
  const users = useDb((d) => d.users);
  const openTask = useOpenTask();
  if (!task) return <Sheet open={open} onClose={closeSheet} title="Task settings">{null}</Sheet>;
  const following = task.watcherIds.includes(me);
  const link = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('t', task.id);
    void navigator.clipboard.writeText(url.toString());
    toast('Task link copied');
    closeSheet();
  };
  return (
    <Sheet open={open} onClose={closeSheet} title="Task settings" full>
      <div className="m-tiles">
        <button type="button" className="m-tile" onClick={link}>
          <Link2 size={20} />
          <span>Copy link</span>
        </button>
        <button type="button" className={clsx('m-tile', { on: fav })} onClick={() => toggleFavorite({ type: 'task', id: task.id })}>
          <Star size={20} fill={fav ? 'currentColor' : 'none'} />
          <span>{fav ? 'Favorited' : 'Favorite'}</span>
        </button>
        <button type="button" className="m-tile" onClick={() => (void navigator.clipboard.writeText(task.id), toast('Task ID copied'), closeSheet())}>
          <Copy size={20} />
          <span>Copy ID</span>
        </button>
      </div>
      <SheetRow icon={following ? <BellOff size={20} /> : <Bell size={20} />} label={following ? 'Unfollow' : 'Follow'} onClick={() => toggleWatcher(task.id)} />
      <SheetRow
        icon={<Users size={20} />}
        label="Followers"
        value={task.watcherIds.length ? task.watcherIds.map((id) => users[id]?.name.split(' ')[0]).filter(Boolean).join(', ') : 'None'}
      />
      {list && (
        <SheetSection title="In List">
          <SheetRow icon={<ListTodo size={20} />} label={list.name} value="Home location" />
        </SheetSection>
      )}
      <SheetSection>
        <SheetRow
          icon={<CopyPlus size={20} />}
          label="Duplicate"
          onClick={() => {
            const d = duplicateTask(task.id);
            closeSheet();
            if (d) openTask(d.id, { replace: true });
          }}
        />
        <SheetRow icon={<Archive size={20} />} label={task.archived ? 'Unarchive' : 'Archive'} onClick={() => (archiveTask(task.id, !task.archived), closeSheet())} />
        <SheetRow
          icon={<Trash2 size={20} />}
          label="Delete"
          danger
          onClick={() => {
            if (!confirm(`Delete "${task.name}"?`)) return;
            closeSheet();
            deleteTask(task.id);
          }}
        />
      </SheetSection>
    </Sheet>
  );
}
